/**
 * Mock Permissions and Roles Data
 * Matches: security.roles, security.permissions tables
 */

import type { Permission, Role } from '@/types/auth.types'
import type { UserRole } from '@/types/user.types'
import { resolveRole } from '@/security/accessContract'

// ============================================================================
// MOCK PERMISSIONS
// ============================================================================

export const mockPermissions: Permission[] = [
  // User Management
  {
    permissionId: 'perm-001',
    permissionKey: 'txh.user.create',
    description: 'Create new users',
    category: 'users',
  },
  {
    permissionId: 'perm-002',
    permissionKey: 'txh.user.read',
    description: 'View user details',
    category: 'users',
  },
  {
    permissionId: 'perm-003',
    permissionKey: 'txh.user.update',
    description: 'Update user information',
    category: 'users',
  },
  {
    permissionId: 'perm-004',
    permissionKey: 'txh.user.delete',
    description: 'Delete users',
    category: 'users',
  },

  // Role Management
  {
    permissionId: 'perm-005',
    permissionKey: 'txh.role.create',
    description: 'Create new roles',
    category: 'roles',
  },
  {
    permissionId: 'perm-006',
    permissionKey: 'txh.role.read',
    description: 'View role details',
    category: 'roles',
  },
  {
    permissionId: 'perm-007',
    permissionKey: 'txh.role.update',
    description: 'Update role information',
    category: 'roles',
  },
  {
    permissionId: 'perm-008',
    permissionKey: 'txh.role.delete',
    description: 'Delete roles',
    category: 'roles',
  },
  {
    permissionId: 'perm-009',
    permissionKey: 'txh.role.assign',
    description: 'Assign roles to users',
    category: 'roles',
  },

  // Client Management
  {
    permissionId: 'perm-010',
    permissionKey: 'txh.client.create',
    description: 'Create new clients',
    category: 'clients',
  },
  {
    permissionId: 'perm-011',
    permissionKey: 'txh.client.read',
    description: 'View client details',
    category: 'clients',
  },
  {
    permissionId: 'perm-012',
    permissionKey: 'txh.client.update',
    description: 'Update client information',
    category: 'clients',
  },
  {
    permissionId: 'perm-013',
    permissionKey: 'txh.client.delete',
    description: 'Delete clients',
    category: 'clients',
  },
  { permissionId: 'perm-client-export', permissionKey: 'txh.client.export', description: 'Export clients', category: 'clients' },
  { permissionId: 'perm-client-pan', permissionKey: 'txh.client.lookup_pan', description: 'Look up PAN details', category: 'clients' },
  { permissionId: 'perm-client-gstin', permissionKey: 'txh.client.lookup_gstin', description: 'Look up GSTIN details', category: 'clients' },
  { permissionId: 'perm-note-read', permissionKey: 'txh.client.note.read', description: 'View customer notes', category: 'clients' },
  { permissionId: 'perm-note-create', permissionKey: 'txh.client.note.create', description: 'Create customer notes', category: 'clients' },
  { permissionId: 'perm-note-delete', permissionKey: 'txh.client.note.delete', description: 'Delete customer notes', category: 'clients' },
  { permissionId: 'perm-client-access-read', permissionKey: 'txh.client.access.read', description: 'View effective client and business entity scope', category: 'clients' },
  { permissionId: 'perm-client-grant-respond', permissionKey: 'txh.client.grant.respond', description: 'Accept or decline representative authorization', category: 'clients' },

  // Document Management
  {
    permissionId: 'perm-014',
    permissionKey: 'txh.document.upload',
    description: 'Upload documents',
    category: 'documents',
  },
  {
    permissionId: 'perm-015',
    permissionKey: 'txh.document.read',
    description: 'View documents',
    category: 'documents',
  },
  {
    permissionId: 'perm-016',
    permissionKey: 'txh.document.delete',
    description: 'Delete documents',
    category: 'documents',
  },
  { permissionId: 'perm-document-download', permissionKey: 'txh.document.download', description: 'Download documents', category: 'documents' },
  { permissionId: 'perm-document-update', permissionKey: 'txh.document.update', description: 'Update documents', category: 'documents' },
  { permissionId: 'perm-document-verify', permissionKey: 'txh.document.verify', description: 'Verify documents', category: 'documents' },
  { permissionId: 'perm-document-search', permissionKey: 'txh.document.search', description: 'Search documents', category: 'documents' },
  { permissionId: 'perm-document-version-read', permissionKey: 'txh.document.version.read', description: 'View document versions', category: 'documents' },

  // Assessment Management
  {
    permissionId: 'perm-017',
    permissionKey: 'txh.assessment.create',
    description: 'Create assessments',
    category: 'assessments',
  },
  {
    permissionId: 'perm-018',
    permissionKey: 'txh.assessment.read',
    description: 'View assessments',
    category: 'assessments',
  },
  {
    permissionId: 'perm-019',
    permissionKey: 'txh.assessment.update',
    description: 'Update assessments',
    category: 'assessments',
  },
  {
    permissionId: 'perm-020',
    permissionKey: 'txh.assessment.approve',
    description: 'Approve assessments',
    category: 'assessments',
  },
  { permissionId: 'perm-assessment-delete', permissionKey: 'txh.assessment.delete', description: 'Delete assessments', category: 'assessments' },
  { permissionId: 'perm-assessment-calculate', permissionKey: 'txh.assessment.calculate', description: 'Calculate tax', category: 'assessments' },
  { permissionId: 'perm-assessment-recalculate', permissionKey: 'txh.assessment.recalculate', description: 'Recalculate tax', category: 'assessments' },
  { permissionId: 'perm-assessment-status', permissionKey: 'txh.assessment.status.update', description: 'Update assessment status', category: 'assessments' },
  { permissionId: 'perm-assessment-file', permissionKey: 'txh.assessment.file', description: 'File assessments', category: 'assessments' },
  { permissionId: 'perm-assessment-work-read', permissionKey: 'txh.assessment.work.read', description: 'View assigned assistant work', category: 'assessments' },
  { permissionId: 'perm-assessment-work-assign', permissionKey: 'txh.assessment.work.assign', description: 'Assign assistant work', category: 'assessments' },
  { permissionId: 'perm-assessment-work-update', permissionKey: 'txh.assessment.work.update', description: 'Update assigned assistant work', category: 'assessments' },
  { permissionId: 'perm-assessment-work-submit', permissionKey: 'txh.assessment.work.submit', description: 'Submit assistant work', category: 'assessments' },
  { permissionId: 'perm-assessment-work-review', permissionKey: 'txh.assessment.work.review', description: 'Review assistant work', category: 'assessments' },
  { permissionId: 'perm-assessment-research-read', permissionKey: 'txh.assessment.research.read', description: 'View assessment research', category: 'assessments' },
  { permissionId: 'perm-assessment-research-create', permissionKey: 'txh.assessment.research.create', description: 'Create assessment research', category: 'assessments' },
  { permissionId: 'perm-assessment-research-update', permissionKey: 'txh.assessment.research.update', description: 'Update assessment research', category: 'assessments' },
  { permissionId: 'perm-assessment-research-submit', permissionKey: 'txh.assessment.research.submit', description: 'Submit assessment research', category: 'assessments' },
  { permissionId: 'perm-assessment-research-review', permissionKey: 'txh.assessment.research.review', description: 'Review assessment research', category: 'assessments' },
  { permissionId: 'perm-assessment-case-create', permissionKey: 'txh.assessment.case.create', description: 'Create V2 return-preparation cases', category: 'assessments' },
  { permissionId: 'perm-assessment-case-read', permissionKey: 'txh.assessment.case.read', description: 'View authorized V2 return-preparation cases', category: 'assessments' },
  { permissionId: 'perm-assessment-case-update', permissionKey: 'txh.assessment.case.update', description: 'Update V2 return-preparation case shells', category: 'assessments' },
  { permissionId: 'perm-assessment-case-invite', permissionKey: 'txh.assessment.case.invite.manage', description: 'Manage purpose-bound case invitations', category: 'assessments' },
  { permissionId: 'perm-assessment-applicability-read', permissionKey: 'txh.assessment.applicability.read', description: 'View case applicability and routing', category: 'assessments' },
  { permissionId: 'perm-assessment-applicability-update', permissionKey: 'txh.assessment.applicability.update', description: 'Answer case applicability questions', category: 'assessments' },
  { permissionId: 'perm-assessment-routing-recompute', permissionKey: 'txh.assessment.routing.recompute', description: 'Recompute governed case routing', category: 'assessments' },
  { permissionId: 'perm-assessment-routing-override', permissionKey: 'txh.assessment.routing.override', description: 'Record a professional routing override', category: 'assessments' },

  // GST Assessments
  { permissionId: 'perm-gst-create', permissionKey: 'txh.gst_assessment.create', description: 'Create GST assessments', category: 'gst_assessments' },
  { permissionId: 'perm-gst-read', permissionKey: 'txh.gst_assessment.read', description: 'View GST assessments', category: 'gst_assessments' },
  { permissionId: 'perm-gst-update', permissionKey: 'txh.gst_assessment.update', description: 'Update GST assessments', category: 'gst_assessments' },
  { permissionId: 'perm-gst-delete', permissionKey: 'txh.gst_assessment.delete', description: 'Delete GST assessments', category: 'gst_assessments' },
  { permissionId: 'perm-gst-summary', permissionKey: 'txh.gst_assessment.summary', description: 'View GST summaries', category: 'gst_assessments' },
  { permissionId: 'perm-gst-status', permissionKey: 'txh.gst_assessment.status.update', description: 'Update GST assessment status', category: 'gst_assessments' },
  { permissionId: 'perm-gst-file', permissionKey: 'txh.gst_assessment.file', description: 'File GST assessments', category: 'gst_assessments' },
  { permissionId: 'perm-gst-approve', permissionKey: 'txh.gst_assessment.approve', description: 'Approve GST assessments', category: 'gst_assessments' },

  // Reports
  {
    permissionId: 'perm-021',
    permissionKey: 'txh.reports.view',
    description: 'View reports',
    category: 'reports',
  },
  {
    permissionId: 'perm-022',
    permissionKey: 'txh.reports.export',
    description: 'Export reports',
    category: 'reports',
  },

  // Settings
  {
    permissionId: 'perm-023',
    permissionKey: 'txh.settings.read',
    description: 'View settings',
    category: 'settings',
  },
  {
    permissionId: 'perm-024',
    permissionKey: 'txh.settings.update',
    description: 'Update settings',
    category: 'settings',
  },
  { permissionId: 'perm-settings-delete', permissionKey: 'txh.settings.delete', description: 'Delete settings', category: 'settings' },

  // Billing
  {
    permissionId: 'perm-025',
    permissionKey: 'txh.settings.billing',
    description: 'Manage billing settings',
    category: 'billing',
  },

  // Audit
  {
    permissionId: 'perm-027',
    permissionKey: 'txh.audit.view',
    description: 'View audit logs',
    category: 'audit',
  },
  { permissionId: 'perm-028', permissionKey: 'txh.audit.read_own', description: 'View own audit activity', category: 'audit' },
  { permissionId: 'perm-029', permissionKey: 'txh.session.read', description: 'View own sessions', category: 'sessions' },
  { permissionId: 'perm-030', permissionKey: 'txh.session.read_any', description: 'View firm user sessions', category: 'sessions' },
  { permissionId: 'perm-031', permissionKey: 'txh.session.revoke', description: 'Revoke an own session', category: 'sessions' },
  { permissionId: 'perm-032', permissionKey: 'txh.session.revoke_all', description: 'Revoke all other own sessions', category: 'sessions' },
  { permissionId: 'perm-033', permissionKey: 'txh.audit.export', description: 'Export audit records', category: 'audit' },
  { permissionId: 'perm-payment-create', permissionKey: 'txh.payment.create', description: 'Create payments', category: 'payments' },
  { permissionId: 'perm-payment-read', permissionKey: 'txh.payment.read', description: 'View payments', category: 'payments' },
  { permissionId: 'perm-payment-update', permissionKey: 'txh.payment.update', description: 'Update payments', category: 'payments' },
  { permissionId: 'perm-payment-delete', permissionKey: 'txh.payment.delete', description: 'Delete payments', category: 'payments' },
  { permissionId: 'perm-payment-refund', permissionKey: 'txh.payment.refund', description: 'Refund payments', category: 'payments' },
  { permissionId: 'perm-payment-reconcile', permissionKey: 'txh.payment.reconcile', description: 'Reconcile payments', category: 'payments' },
  { permissionId: 'perm-reminder-create', permissionKey: 'txh.reminder.create', description: 'Create reminders', category: 'reminders' },
  { permissionId: 'perm-reminder-read', permissionKey: 'txh.reminder.read', description: 'View reminders', category: 'reminders' },
  { permissionId: 'perm-reminder-update', permissionKey: 'txh.reminder.update', description: 'Update reminders', category: 'reminders' },
  { permissionId: 'perm-reminder-delete', permissionKey: 'txh.reminder.delete', description: 'Delete reminders', category: 'reminders' },
  { permissionId: 'perm-reminder-status', permissionKey: 'txh.reminder.status.update', description: 'Update reminder status', category: 'reminders' },
  { permissionId: 'perm-notification-read', permissionKey: 'txh.notification.read', description: 'View notifications', category: 'notifications' },
  { permissionId: 'perm-insight-create', permissionKey: 'txh.insight.create', description: 'Create insights', category: 'insights' },
  { permissionId: 'perm-insight-read', permissionKey: 'txh.insight.read', description: 'View insights', category: 'insights' },
  { permissionId: 'perm-insight-update', permissionKey: 'txh.insight.update', description: 'Update insights', category: 'insights' },
  { permissionId: 'perm-insight-generate', permissionKey: 'txh.insight.generate', description: 'Generate insights', category: 'insights' },
  { permissionId: 'perm-insight-dismiss', permissionKey: 'txh.insight.dismiss', description: 'Dismiss insights', category: 'insights' },
  { permissionId: 'perm-insight-apply', permissionKey: 'txh.insight.apply', description: 'Apply insights', category: 'insights' },
  { permissionId: 'perm-insight-delete', permissionKey: 'txh.insight.delete', description: 'Delete insights', category: 'insights' },
  { permissionId: 'perm-branch-read', permissionKey: 'txh.branch.read', description: 'View branches', category: 'branches' },
  { permissionId: 'perm-branch-assign', permissionKey: 'txh.branch.assign', description: 'Assign branches', category: 'branches' },
  { permissionId: 'perm-branch-create', permissionKey: 'txh.branch.create', description: 'Create branches', category: 'governance' },
  { permissionId: 'perm-team-create', permissionKey: 'txh.team.create', description: 'Create teams', category: 'governance' },
  { permissionId: 'perm-team-read', permissionKey: 'txh.team.read', description: 'View teams', category: 'governance' },
  { permissionId: 'perm-team-assign', permissionKey: 'txh.team.assign', description: 'Assign team membership', category: 'governance' },
  { permissionId: 'perm-portfolio-create', permissionKey: 'txh.portfolio.create', description: 'Create portfolios', category: 'governance' },
  { permissionId: 'perm-portfolio-read', permissionKey: 'txh.portfolio.read', description: 'View portfolios', category: 'governance' },
  { permissionId: 'perm-portfolio-update', permissionKey: 'txh.portfolio.update', description: 'Update portfolios', category: 'governance' },
  { permissionId: 'perm-portfolio-assign', permissionKey: 'txh.portfolio.assign', description: 'Assign portfolio membership', category: 'governance' },
  { permissionId: 'perm-governance-policy-read', permissionKey: 'txh.governance.policy.read', description: 'View cross-branch policies', category: 'governance' },
  { permissionId: 'perm-governance-policy-manage', permissionKey: 'txh.governance.policy.manage', description: 'Manage cross-branch policies', category: 'governance' },
  { permissionId: 'perm-review-policy-read', permissionKey: 'txh.governance.review_policy.read', description: 'View review policies', category: 'governance' },
  { permissionId: 'perm-review-policy-manage', permissionKey: 'txh.governance.review_policy.manage', description: 'Manage review policies', category: 'governance' },
  { permissionId: 'perm-delegation-read', permissionKey: 'txh.governance.delegation.read', description: 'View review delegations', category: 'governance' },
  { permissionId: 'perm-delegation-manage', permissionKey: 'txh.governance.delegation.manage', description: 'Manage review delegations', category: 'governance' },
  { permissionId: 'perm-workload-read', permissionKey: 'txh.governance.workload.read', description: 'View workload and overdue reporting', category: 'governance' },
  { permissionId: 'perm-escalation-manage', permissionKey: 'txh.governance.escalation.manage', description: 'Run workflow escalations', category: 'governance' },
]

// ============================================================================
// MOCK ROLES
// ============================================================================

const allPermissionKeys = mockPermissions.map((permission) => permission.permissionKey)
const ownSecurityPermissions = [
  'txh.audit.read_own',
  'txh.session.read',
  'txh.session.revoke',
  'txh.session.revoke_all',
]
const clientPermissions = [
  'txh.client.read',
  'txh.document.upload',
  'txh.document.read',
  'txh.document.download',
  'txh.document.search',
  'txh.document.version.read',
  'txh.assessment.read',
  'txh.assessment.calculate',
  'txh.reminder.read',
  'txh.payment.read',
  'txh.notification.read',
  ...ownSecurityPermissions,
]
const individualV2Permissions = [
  'txh.assessment.case.read',
  'txh.assessment.applicability.read',
  'txh.assessment.applicability.update',
  'txh.assessment.routing.recompute',
]

export const mockRoles: Role[] = [
  {
    roleId: 'role-001',
    roleName: 'owner',
    roleType: 'predefined',
    description: 'Firm owner with full access including billing and settings',
    isSystemRole: true,
    permissions: allPermissionKeys,
  },
  {
    roleId: 'role-002',
    roleName: 'admin',
    roleType: 'predefined',
    description: 'Administrator with user management and configuration access',
    isSystemRole: true,
    permissions: allPermissionKeys.filter(
      (permission) =>
        permission !== 'txh.settings.billing' &&
        permission !== 'txh.assessment.routing.override'
    ),
  },
  {
    roleId: 'role-003',
    roleName: 'partner',
    roleType: 'predefined',
    description: 'Partner / CA with client management and filing access',
    isSystemRole: true,
    permissions: [
      ...allPermissionKeys.filter((permission) =>
        ['txh.client.', 'txh.document.', 'txh.assessment.', 'txh.gst_assessment.',
          'txh.reminder.', 'txh.notification.', 'txh.insight.', 'txh.reports.']
          .some((prefix) => permission.startsWith(prefix))
      ),
      'txh.user.read',
      'txh.payment.read',
      'txh.branch.read',
      'txh.branch.assign',
      ...allPermissionKeys.filter((permission) =>
        permission.startsWith('txh.team.') || permission.startsWith('txh.portfolio.') ||
        permission.startsWith('txh.governance.')
      ),
      ...ownSecurityPermissions,
    ],
  },
  {
    roleId: 'role-004',
    roleName: 'assistant',
    roleType: 'predefined',
    description: 'Assistant with limited operational access',
    isSystemRole: true,
    permissions: [
      'txh.client.read', 'txh.client.update',
      'txh.client.lookup_pan', 'txh.client.lookup_gstin',
      'txh.client.note.read', 'txh.client.note.create',
      'txh.document.upload', 'txh.document.read', 'txh.document.download',
      'txh.document.update', 'txh.document.search', 'txh.document.version.read',
      'txh.assessment.create', 'txh.assessment.read', 'txh.assessment.update',
      'txh.assessment.calculate', 'txh.assessment.recalculate', 'txh.assessment.status.update',
      'txh.assessment.work.read', 'txh.assessment.work.update', 'txh.assessment.work.submit',
      'txh.assessment.case.read', 'txh.assessment.case.update',
      'txh.assessment.applicability.read', 'txh.assessment.applicability.update',
      'txh.assessment.routing.recompute',
      'txh.gst_assessment.create', 'txh.gst_assessment.read', 'txh.gst_assessment.update',
      'txh.gst_assessment.summary', 'txh.gst_assessment.status.update',
      'txh.reminder.create', 'txh.reminder.read', 'txh.reminder.update',
      'txh.reminder.status.update', 'txh.notification.read',
      ...ownSecurityPermissions,
    ],
  },
  {
    roleId: 'role-005',
    roleName: 'staff',
    roleType: 'predefined',
    description: 'Staff with basic task-level access',
    isSystemRole: true,
    permissions: [
      'txh.client.read', 'txh.document.read', 'txh.assessment.read',
      'txh.assessment.calculate', 'txh.reminder.read', ...ownSecurityPermissions,
    ],
  },
  {
    roleId: 'role-006',
    roleName: 'client',
    roleType: 'predefined',
    description: 'External client with read-only access to own data',
    isSystemRole: true,
    permissions: clientPermissions,
  },
  {
    roleId: 'role-007', roleName: 'research_assistant', roleType: 'predefined',
    description: 'Research and advisory support without filing authority', isSystemRole: true,
    permissions: [
      'txh.client.read', 'txh.document.read', 'txh.document.download',
      'txh.document.search', 'txh.document.version.read',
      'txh.client.lookup_pan', 'txh.client.lookup_gstin', 'txh.client.note.read',
      'txh.assessment.read', 'txh.assessment.calculate',
      'txh.assessment.work.read', 'txh.assessment.work.update', 'txh.assessment.work.submit',
      'txh.assessment.research.read', 'txh.assessment.research.create',
      'txh.assessment.research.update', 'txh.assessment.research.submit',
      'txh.gst_assessment.read', 'txh.gst_assessment.summary',
      'txh.insight.create', 'txh.insight.read', 'txh.insight.update',
      'txh.insight.generate', 'txh.insight.dismiss', 'txh.insight.apply',
      'txh.reports.view', ...ownSecurityPermissions,
    ],
  },
  {
    roleId: 'role-008', roleName: 'individual_client', roleType: 'predefined',
    description: 'Individual taxpayer self-service access', isSystemRole: true,
    permissions: [...clientPermissions, ...individualV2Permissions],
  },
  {
    roleId: 'role-009', roleName: 'business_owner_client', roleType: 'predefined',
    description: 'Business owner access to linked entities', isSystemRole: true,
    permissions: [...clientPermissions, 'txh.client.access.read', 'txh.gst_assessment.read', 'txh.gst_assessment.summary'],
  },
  {
    roleId: 'role-010', roleName: 'authorized_client', roleType: 'predefined',
    description: 'Authorized representative with scoped access', isSystemRole: true,
    permissions: [
      ...clientPermissions,
      ...individualV2Permissions,
      'txh.client.access.read',
      'txh.client.grant.respond',
      'txh.gst_assessment.read',
      'txh.gst_assessment.summary',
    ],
  },
]

// ============================================================================
// PERMISSION CATEGORIES (for grouping in UI)
// ============================================================================

export const permissionCategories = [
  {
    key: 'governance',
    label: 'Practice Governance',
    description: 'Manage organization scope, review controls, and workload',
    permissions: mockPermissions.filter((p) => p.category === 'governance'),
  },
  {
    key: 'users',
    label: 'User Management',
    description: 'Manage users in the organization',
    permissions: mockPermissions.filter((p) => p.category === 'users'),
  },
  {
    key: 'roles',
    label: 'Role Management',
    description: 'Manage roles and role assignments',
    permissions: mockPermissions.filter((p) => p.category === 'roles'),
  },
  {
    key: 'clients',
    label: 'Client Management',
    description: 'Manage client records and information',
    permissions: mockPermissions.filter((p) => p.category === 'clients'),
  },
  {
    key: 'documents',
    label: 'Document Management',
    description: 'Upload, view, and manage documents',
    permissions: mockPermissions.filter((p) => p.category === 'documents'),
  },
  {
    key: 'assessments',
    label: 'Assessment Management',
    description: 'Create and manage tax assessments',
    permissions: mockPermissions.filter((p) => p.category === 'assessments'),
  },
  {
    key: 'reports',
    label: 'Reports',
    description: 'View and export reports',
    permissions: mockPermissions.filter((p) => p.category === 'reports'),
  },
  {
    key: 'settings',
    label: 'Settings',
    description: 'View and update organization settings',
    permissions: mockPermissions.filter((p) => p.category === 'settings'),
  },
  {
    key: 'billing',
    label: 'Billing',
    description: 'View and manage billing information',
    permissions: mockPermissions.filter((p) => p.category === 'billing'),
  },
  {
    key: 'audit',
    label: 'Audit',
    description: 'View audit logs and activity',
    permissions: mockPermissions.filter((p) => p.category === 'audit'),
  },
]

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get role by name
 */
export function getRoleByName(roleName: UserRole): Role | undefined {
  const requested = resolveRole(roleName)
  return mockRoles.find((role) => {
    if (role.roleName === roleName) return true
    const candidate = resolveRole(role.roleName)
    return requested.canonicalRole !== null && candidate.canonicalRole === requested.canonicalRole
  })
}

/**
 * Get role by ID
 */
export function getRoleById(roleId: string): Role | undefined {
  return mockRoles.find((r) => r.roleId === roleId)
}

/**
 * Get permission by key
 */
export function getPermissionByKey(permissionKey: string): Permission | undefined {
  return mockPermissions.find((p) => p.permissionKey === permissionKey)
}

/**
 * Get permissions by category
 */
export function getPermissionsByCategory(category: string): Permission[] {
  return mockPermissions.filter((p) => p.category === category)
}

/**
 * Get all permissions for a role
 */
export function getPermissionsForRole(roleName: UserRole): Permission[] {
  const role = getRoleByName(roleName)
  if (!role) return []
  return mockPermissions.filter((p) => role.permissions.includes(p.permissionKey))
}

/**
 * Check if role has permission
 */
export function roleHasPermission(roleName: UserRole, permissionKey: string): boolean {
  const role = getRoleByName(roleName)
  if (!role) return false
  return role.permissions.includes(permissionKey)
}

/**
 * Get role display info
 */
export function getRoleDisplayInfo(roleName: UserRole): {
  label: string
  description: string
  color: string
  permissionCount: number
} {
  const role = getRoleByName(roleName)
  const colors: Partial<Record<UserRole, string>> = {
    owner: 'primary',
    admin: 'secondary',
    partner: 'accent',
    assistant: 'info',
    staff: 'default',
    client: 'ghost',
  }

  return {
    label: role?.roleName || roleName,
    description: role?.description || '',
    color: colors[roleName] || 'default',
    permissionCount: role?.permissions.length || 0,
  }
}

/**
 * Get available roles for assignment (excluding owner)
 */
export function getAssignableRoles(): Role[] {
  return mockRoles.filter((r) => r.roleName !== 'owner')
}

/**
 * Get roles that can be created (custom roles only)
 */
export function getCustomRoles(): Role[] {
  return mockRoles.filter((r) => r.roleType === 'custom')
}

/**
 * Get system (predefined) roles
 */
export function getSystemRoles(): Role[] {
  return mockRoles.filter((r) => r.isSystemRole)
}
